import type { TimelineSession } from '@/types/coach';

interface Props {
  session: TimelineSession;
  index: number; // 1-basiert; index === 1 → "Erstgespräch"
  onClick: () => void;
}

const DATE_FORMATTER = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const FORMAT_LABEL: Record<TimelineSession['format'], string> = {
  in_person: 'Vor Ort',
  phone: 'Telefon',
  video: 'Video',
};

export function TimelineSessionCard({ session, index, onClick }: Props) {
  const title = index === 1 ? 'Erstgespräch' : `Session ${index}`;
  const dateLabel = DATE_FORMATTER.format(new Date(session.date));
  const durationLabel = session.durationMinutes != null ? `${session.durationMinutes} Min` : null;
  const formatLabel = FORMAT_LABEL[session.format];
  const metaParts = [dateLabel, durationLabel, formatLabel].filter(Boolean);

  return (
    <div className="relative pl-6">
      <span className="absolute left-[1px] top-2 inline-block h-3 w-3 rounded-full border-2 border-primary bg-background" />
      <button
        type="button"
        onClick={onClick}
        className="block w-full rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-foreground/40"
      >
        <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {metaParts.join(' · ')}
        </div>
        <div className="mt-0.5 text-sm font-semibold">{title}</div>

        {session.hasDebrief ? (
          <>
            {session.keyInsightsSnippet && (
              <p className="mt-2 text-xs leading-relaxed text-foreground/80">
                {session.keyInsightsSnippet}
              </p>
            )}
            {(session.moodSnippet || session.nextStepsSnippet) && (
              <div className="mt-2 grid gap-1 border-t border-border/60 pt-2">
                {session.moodSnippet && (
                  <div className="text-xs text-foreground/80">
                    <span className="text-muted-foreground">Stimmung:</span>{' '}
                    {session.moodSnippet}
                  </div>
                )}
                {session.nextStepsSnippet && (
                  <div className="text-xs text-foreground/80">
                    <span className="text-muted-foreground">Nächster Schritt:</span>{' '}
                    {session.nextStepsSnippet}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <p className="mt-2 text-xs italic text-muted-foreground">
            Keine Nachbereitung erfasst.
          </p>
        )}
      </button>
    </div>
  );
}
